import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { listAllEvents, createEvent, updateEvent, deleteEvent } from '../../services/eventService';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import '../../styles/EventManagement.css';

export default function StaffEventManagement() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm();

  const fetchEvents = useCallback(async () => {
    try {
      const res = await listAllEvents();
      if (res.success) setEvents(res.data);
    } catch { setError('Erreur lors du chargement des événements'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const onSubmit = async (data) => {
    setError('');
    setSuccess('');
    try {
      if (editingId) {
        await updateEvent(editingId, data);
        setSuccess('Événement mis à jour');
      } else {
        await createEvent(data);
        setSuccess('Événement créé');
      }
      reset();
      setShowForm(false);
      setEditingId(null);
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur');
    }
  };

  const handleEdit = (event) => {
    setEditingId(event.id);
    setValue('title', event.title);
    setValue('description', event.description || '');
    setValue('eventType', event.eventType || '');
    setValue('eventDate', event.eventDate?.split('T')[0] || '');
    setValue('maxParticipants', event.maxParticipants || '');
    setValue('registrationDeadline', event.registrationDeadline?.split('T')[0] || '');
    setValue('ndaRequired', event.ndaRequired || false);
    setValue('status', event.status || 'draft');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await deleteEvent(id);
      setSuccess('Événement supprimé');
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors de la suppression');
    }
  };

  const copyPreRegLink = (eventId) => {
    const link = `${window.location.origin}/register?event=${eventId}`;
    navigator.clipboard.writeText(link).then(() => setSuccess('Lien copié !'));
  };

  if (loading) return <LoadingSkeleton lines={8} />;

  return (
    <div className="event-management-page">
      <div className="toolbar">
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditingId(null); reset(); }}>
          {showForm ? 'Annuler' : '+ Nouvel événement'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {showForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="event-form-card">
          <h3>{editingId ? 'Modifier l\'événement' : 'Nouvel événement'}</h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Titre *</label>
              <input {...register('title', { required: 'Requis' })} placeholder="Titre de l'événement" />
              {errors.title && <span className="field-error">{errors.title.message}</span>}
            </div>
            <div className="form-group">
              <label>Type d'événement *</label>
              <select {...register('eventType', { required: 'Requis' })}>
                <option value="">— Sélectionnez —</option>
                <option value="workshop">Atelier (Workshop)</option>
                <option value="networking">Networking</option>
                <option value="meeting">Réunion</option>
                <option value="presentation">Présentation</option>
                <option value="other">Autre</option>
              </select>
              {errors.eventType && <span className="field-error">{errors.eventType.message}</span>}
            </div>
          </div>

          <div className="form-grid full-width">
            <div className="form-group">
              <label>Description</label>
              <textarea {...register('description')} rows={3} placeholder="Description de l'événement" />
            </div>
          </div>

          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label>Date *</label>
              <input type="date" {...register('eventDate', { required: 'Requis' })} />
              {errors.eventDate && <span className="field-error">{errors.eventDate.message}</span>}
            </div>
            <div className="form-group">
              <label>Participants max *</label>
              <input type="number" {...register('maxParticipants', { required: 'Requis', min: { value: 1, message: 'Min 1' } })} placeholder="Ex: 50" />
              {errors.maxParticipants && <span className="field-error">{errors.maxParticipants.message}</span>}
            </div>
            <div className="form-group">
              <label>Date limite d'inscription *</label>
              <input type="date" {...register('registrationDeadline', { required: 'Requis' })} />
              {errors.registrationDeadline && <span className="field-error">{errors.registrationDeadline.message}</span>}
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Statut</label>
              <select {...register('status')}>
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
            <div className="form-group checkbox-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
              <label className="checkbox-label">
                <input type="checkbox" {...register('ndaRequired')} />
                NDA requis
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'En cours...' : editingId ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      )}

      {!showForm && (
        <div className="events-table-container">
          {events.length === 0 ? (
            <p className="no-data">Aucun événement</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Participants</th>
                  <th>Statut</th>
                  <th>NDA</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => (
                  <tr key={ev.id}>
                    <td>{ev.title}</td>
                    <td>{ev.eventType || '—'}</td>
                    <td>{new Date(ev.eventDate).toLocaleDateString('fr-FR')}</td>
                    <td>{ev.registrationCount || 0}{ev.maxParticipants ? `/${ev.maxParticipants}` : ''}</td>
                    <td>
                      <span className={`badge badge-${ev.status}`}>{ev.status}</span>
                    </td>
                    <td>{ev.ndaRequired ? 'Oui' : 'Non'}</td>
                    <td className="actions-cell">
                      <button className="btn btn-sm" onClick={() => copyPreRegLink(ev.id)} title="Copier le lien de pré-inscription">🔗</button>
                      <button className="btn btn-sm" onClick={() => handleEdit(ev)}>✏️</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(ev.id)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
